/**
 * Logout Service
 *
 * Handles backend notification during logout flow.
 * This service ensures the backend is informed when a user logs out,
 * allowing for:
 * - Server-side session invalidation
 * - Token blacklisting (prevents stolen tokens from working after logout)
 * - HttpOnly cookie clearing (done via Set-Cookie response headers)
 * - Audit trail creation
 *
 * SECURITY: This closes a critical gap where logout previously only cleared
 * frontend state, leaving backend sessions and tokens valid until natural expiry.
 *
 * DESIGN: Backend failures are handled gracefully - logout should never be
 * blocked by backend issues. We log warnings for monitoring but proceed
 * with frontend cleanup regardless.
 */

import { apiCall, getAuthTokenWithActivity } from '@/utils/apiUtils';
import { devLog, devWarn } from '@/utils/devLog';

/**
 * Result of a backend logout notification attempt
 */
export interface LogoutResult {
  /** Whether the overall logout flow should proceed (always true) */
  success: boolean;
  /** Whether backend was successfully notified */
  backendNotified: boolean;
  /** Error message if backend notification failed */
  error?: string;
  /** Version info from backend response (for validation/debugging) */
  version?: {
    app: string;
    buildTime: string;
  };
}

/**
 * Backend logout response shape
 */
interface BackendLogoutResponse {
  success: boolean;
  message?: string;
  version?: {
    app: string;
    buildTime: string;
  };
  error?: string;
}

/**
 * Notify the backend about logout.
 *
 * This function calls POST /api/auth/logout to:
 * 1. Blacklist the current session token in Redis
 * 2. Invalidate server-side auth cache
 * 3. Clear HttpOnly cookies via Set-Cookie headers
 * 4. Create audit log entry
 *
 * IMPORTANT: This function NEVER throws. Backend failures are logged but
 * don't block the logout flow. The function always returns a result object.
 *
 * @returns Promise<LogoutResult> - Result indicating backend notification status
 *
 * @example
 * ```typescript
 * const result = await notifyBackendLogout();
 * if (!result.backendNotified) {
 *   console.warn('Backend not notified:', result.error);
 * }
 * // Proceed with frontend cleanup regardless
 * ```
 */
export async function notifyBackendLogout(): Promise<LogoutResult> {
  const startTime = Date.now();
  devLog('[LogoutService] Starting backend logout notification...');

  try {
    // Check if we have a valid token before attempting the call
    // If no token, we can skip the backend call (user may already be logged out)
    const token = await getAuthTokenWithActivity(false);
    if (!token) {
      devLog('[LogoutService] No auth token available, skipping backend notification');
      return {
        success: true,
        backendNotified: false,
        error: 'No auth token available - user may already be logged out',
      };
    }

    // Make the API call with credentials to ensure cookies are sent/received
    // The apiCall helper automatically includes the auth token and handles retries
    const response = await apiCall('/api/auth/logout', {
      method: 'POST',
      credentials: 'include', // Important: allows Set-Cookie headers to be processed
    });

    const duration = Date.now() - startTime;

    if (response.ok) {
      try {
        const data: BackendLogoutResponse = await response.json();
        devLog(`[LogoutService] Backend logout successful in ${duration}ms`, data);

        return {
          success: true,
          backendNotified: true,
          version: data.version,
        };
      } catch (parseError) {
        // Response was OK but couldn't parse JSON - still consider it a success
        devWarn('[LogoutService] Backend returned OK but response was not valid JSON');
        return {
          success: true,
          backendNotified: true,
        };
      }
    }

    // Handle non-OK responses
    let errorMessage = `Backend returned status ${response.status}`;

    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch {
      // Couldn't parse error response, use status-based message
    }

    // Log based on status code severity
    if (response.status === 401 || response.status === 403) {
      // Token already invalid/expired - not really an error for logout
      devLog(`[LogoutService] Token already invalid (${response.status}), backend notification skipped`);
      return {
        success: true,
        backendNotified: false,
        error: 'Token already invalid or expired',
      };
    }

    devWarn(`[LogoutService] Backend logout failed after ${duration}ms:`, errorMessage);
    return {
      success: true,
      backendNotified: false,
      error: errorMessage,
    };

  } catch (error) {
    const duration = Date.now() - startTime;

    // Network errors, timeouts, etc.
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    devWarn(`[LogoutService] Backend logout request failed after ${duration}ms:`, errorMessage);

    // Return success=true because logout should proceed even if backend fails
    return {
      success: true,
      backendNotified: false,
      error: errorMessage,
    };
  }
}

/**
 * Utility to check if backend was notified in the last logout attempt.
 * Useful for analytics/monitoring to track backend notification success rate.
 */
let lastLogoutResult: LogoutResult | null = null;

/**
 * Notify backend and store the result for later inspection.
 * This is the primary entry point for logout flows.
 *
 * @returns Promise<LogoutResult>
 */
export async function notifyBackendLogoutWithTracking(): Promise<LogoutResult> {
  lastLogoutResult = await notifyBackendLogout();
  return lastLogoutResult;
}

/**
 * Get the result of the last logout notification attempt.
 * Returns null if no logout has been attempted in this session.
 */
export function getLastLogoutResult(): LogoutResult | null {
  return lastLogoutResult;
}

/**
 * Clear the stored logout result.
 * Called during login to reset state.
 */
export function clearLastLogoutResult(): void {
  lastLogoutResult = null;
}
